import type { InboxItem } from './types';

export type ParsedMetric = {
    label: string;
    value: string | number;
    highlight?: 'success' | 'error' | 'warning' | 'default';
};

export type ParsedErrorLine = {
    rank: number;
    count: number;
    pct?: number;
    message: string;
};

export type ParsedNotification = {
    kind: 'marathon' | 'ddex' | 'generic';
    jobType?: string;
    checkpoint?: string;
    /** Raw checkpoint `last_stop_reason` (e.g. quota, timeout, completed). */
    stopReason?: string;
    window?: string;
    metrics: ParsedMetric[];
    topErrors: ParsedErrorLine[];
    /** True when body contains `Top errors: none`. */
    topErrorsNone?: boolean;
    rawLines: string[];
};

const DASH_PLACEHOLDER = /^[-–—]$|^none$/i;

export function marathonStopReasonLabel(raw?: string): { title: string; detail: string; severity: 'success' | 'warning' | 'error' | 'info' } {
    const code = (raw ?? '').trim().toLowerCase();
    if (!code || DASH_PLACEHOLDER.test(code)) {
        return {
            title: 'Stop reason not recorded',
            detail: 'This run did not save a last stop reason on the marathon checkpoint.',
            severity: 'info',
        };
    }
    switch (code) {
        case 'completed':
            return {
                title: 'Completed normally',
                detail: 'The marathon finished its scheduled pass (queue empty or run marked complete).',
                severity: 'success',
            };
        case 'timeout':
            return {
                title: 'Stopped — time limit',
                detail: 'The run hit the marathon time limit and paused until the next schedule.',
                severity: 'warning',
            };
        case 'quota':
            return {
                title: 'Stopped — API quota / rate limit',
                detail: 'Facebook or platform API quota or rate limits blocked further calls this run.',
                severity: 'warning',
            };
        case 'budget':
            return {
                title: 'Stopped — API call budget',
                detail: 'The run stopped to stay within the configured API call budget for this marathon.',
                severity: 'warning',
            };
        case 'error':
            return {
                title: 'Stopped — error',
                detail: 'The marathon exited because of an unexpected error during processing.',
                severity: 'error',
            };
        case 'incomplete':
            return {
                title: 'Stopped — incomplete',
                detail: 'The run did not finish the full pass (interrupted or ended early).',
                severity: 'warning',
            };
        default:
            return {
                title: `Stopped — ${raw}`,
                detail: `Last checkpoint reason code: ${raw}`,
                severity: 'info',
            };
    }
}

const metricHighlight = (label: string): ParsedMetric['highlight'] => {
    const l = label.toLowerCase();
    if (l.includes('success') || l.includes('updated')) return 'success';
    if (l.includes('failed')) return 'error';
    if (l.includes('remaining')) return 'warning';
    return 'default';
};

const topErrorRe = /^\s*(\d+)\.\s*\((\d+),\s*(\d+)%\)\s*(.+)$/;

export function parseNotificationBody(body: string, title: string): ParsedNotification {
    const rawLines = body.split('\n').map((l) => l.trim()).filter(Boolean);
    const metrics: ParsedMetric[] = [];
    const topErrors: ParsedErrorLine[] = [];
    let jobType: string | undefined;
    let checkpoint: string | undefined;
    let stopReason: string | undefined;
    let window: string | undefined;
    let topErrorsNone = false;
    let inErrors = false;

    for (const line of rawLines) {
        if (line.startsWith('Job:')) {
            jobType = line.slice('Job:'.length).trim();
            continue;
        }
        if (line.startsWith('Checkpoint:')) {
            checkpoint = line.slice('Checkpoint:'.length).trim();
            continue;
        }
        if (line.startsWith('Last stop:')) {
            stopReason = line.slice('Last stop:'.length).trim();
            continue;
        }
        if (/^Top errors:\s*none\s*$/i.test(line)) {
            topErrorsNone = true;
            inErrors = false;
            continue;
        }
        if (line.startsWith('Window:')) {
            window = line.slice('Window:'.length).trim();
            continue;
        }
        if (line === 'Top errors:' || line.startsWith('Top errors:')) {
            inErrors = true;
            continue;
        }
        const errMatch = line.match(topErrorRe);
        if (inErrors && errMatch) {
            topErrors.push({
                rank: Number(errMatch[1]),
                count: Number(errMatch[2]),
                pct: Number(errMatch[3]),
                message: errMatch[4].trim(),
            });
            continue;
        }
        const colon = line.indexOf(':');
        if (colon > 0 && !line.startsWith('http')) {
            const label = line.slice(0, colon).trim();
            const value = line.slice(colon + 1).trim();
            if (/^\d+$/.test(value) || value.includes('→') || label.length < 40) {
                if (!inErrors && label.toLowerCase() !== 'last stop') {
                    metrics.push({
                        label,
                        value: /^\d+$/.test(value) ? Number(value) : value,
                        highlight: metricHighlight(label),
                    });
                }
            }
        }
    }

    const isMarathon =
        jobType != null ||
        title.toLowerCase().includes('marathon') ||
        rawLines.some((l) => l.startsWith('Eligible creators:'));

    const isDdex =
        title.toLowerCase().includes('ddex') ||
        /^UPC\s+\S+:/i.test(body);

    let kind: ParsedNotification['kind'] = 'generic';
    if (isMarathon) kind = 'marathon';
    else if (isDdex) kind = 'ddex';

    return {
        kind,
        jobType,
        checkpoint,
        stopReason,
        window,
        metrics,
        topErrors,
        topErrorsNone,
        rawLines,
    };
}

export function parseDdexUpc(body: string): string | undefined {
    const m = body.match(/^UPC\s+(\S+)\s*:/im);
    return m?.[1];
}

export function notificationKindLabel(item: InboxItem, parsed: ParsedNotification): string {
    if (parsed.kind === 'marathon') {
        switch (parsed.jobType) {
            case 'ig-demographics':
                return 'Instagram demographics';
            case 'tt-demographics':
                return 'TikTok demographics';
            case 'yt-demographics':
                return 'YouTube demographics';
            case 'ig-engagement':
                return 'Instagram engagement';
            default:
                return 'Marathon job';
        }
    }
    if (parsed.kind === 'ddex') return 'DDEX delivery';
    return 'Notification';
}
